import { useState, useEffect } from 'react'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { supabase } from '../services/supabase'
import { secureLogin } from '../services/authSecurity'
import Card from '../components/Card'
import Button from '../components/Button'
import Input from '../components/Input'
import { Heart, Mail, Lock, User, AlertCircle, Eye, EyeOff } from 'lucide-react'
import { APP_NAME } from '../utils/constants'
import { useToast } from '../contexts/ToastContext'
import { useTranslation } from 'react-i18next'

const AuthPage = () => {
  const { t } = useTranslation()
  const { success, error: showError } = useToast()
  const [isLogin, setIsLogin] = useState(() => {
    // Restore login state from localStorage
    const saved = localStorage.getItem('authPage_isLogin')
    return saved ? saved === 'true' : true
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [dataRestored, setDataRestored] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [formData, setFormData] = useState(() => {
    // Restore form data from localStorage
    try {
      const saved = localStorage.getItem('authPage_formData')
      if (saved) {
        const parsed = JSON.parse(saved)
        // Don't restore password for security
        const restored = {
          email: parsed.email || '',
          password: '',
          name: parsed.name || '',
        }
        // Show indicator if data was restored
        if (parsed.email || parsed.name) {
          setTimeout(() => setDataRestored(true), 100)
          setTimeout(() => setDataRestored(false), 3000)
        }
        return restored
      }
    } catch (e) {
      console.error('Error restoring form data:', e)
    }
    return {
      email: '',
      password: '',
      name: '',
    }
  })
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()

  // Save form data to localStorage whenever it changes (except password)
  useEffect(() => {
    const dataToSave = {
      email: formData.email,
      name: formData.name,
      // Don't save password for security
    }
    localStorage.setItem('authPage_formData', JSON.stringify(dataToSave))
    localStorage.setItem('authPage_isLogin', String(isLogin))
  }, [formData.email, formData.name, isLogin])

  // Prevent page reload on visibility change (mobile lock/unlock)
  useEffect(() => {
    const handleVisibilityChange = () => {
      // Don't reload on visibility change - just keep the app running
      if (document.visibilityState === 'visible') {
        // Page became visible again - data should be restored from localStorage
        console.log('Page visible - form data should be restored')
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  // Clear saved data on successful submission
  const clearSavedData = () => {
    localStorage.removeItem('authPage_formData')
    localStorage.removeItem('authPage_isLogin')
  }

  // Redirect to onboarding if invite/token present
  useEffect(() => {
    const invite = searchParams.get('invite')
    const token = searchParams.get('token')
    if ((invite || token) && !isLogin) {
      navigate(`/onboarding?${invite ? `invite=${invite}` : `token=${token}`}`)
    }
  }, [searchParams, isLogin, navigate])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      if (isLogin) {
        // Use secure login with rate limiting
        const { data, error } = await secureLogin(formData.email, formData.password)
        if (error) throw error
        clearSavedData() // Clear saved data on success
        navigate(location.state?.from || '/dashboard')
      } else {
        const { error: signUpError } = await supabase.auth.signUp({
          email: formData.email,
          password: formData.password,
          options: {
            data: {
              name: formData.name,
            },
          },
        })
        if (signUpError) throw signUpError
        
        // Organization will be created automatically via database trigger
        // or we can create it here if trigger is disabled
        clearSavedData() // Clear saved data on success
        
        success(t('auth.register.successMessage'), {
          title: t('auth.register.successTitle'),
          duration: 5000,
        })
      }
    } catch (err) {
      const errorMessage = err.message || t('common.messages.error')
      showError(errorMessage, {
        title: isLogin ? t('auth.login.errorTitle') : t('auth.register.errorTitle'),
      })
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const handleForgotPassword = async () => {
    if (!formData.email) {
      showError(t('auth.login.emailRequired'), {
        title: t('auth.login.emailRequiredTitle'),
      })
      return
    }

    try {
      setLoading(true)
      setError('')

      const redirectTo = `${window.location.origin}/reset-password`
      const { error } = await supabase.auth.resetPasswordForEmail(formData.email, {
        redirectTo,
      })

      if (error) throw error

      success(t('auth.login.resetLinkSent'), {
        title: t('auth.login.resetLinkSentTitle'),
        duration: 6000,
      })
    } catch (err) {
      const message =
        err.message ||
        t('auth.login.resetFailed')
      showError(message, { title: t('auth.login.resetFailedTitle') })
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <Card variant="glass" className="p-8 shadow-2xl">
          {/* Brand Logo/Header */}
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ delay: 0.1, type: 'spring', stiffness: 200 }}
              className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-primary-500 via-primary-600 to-secondary-500 flex items-center justify-center shadow-lg dark:shadow-primary-500/20"
            >
              <Heart size={32} className="text-white" fill="currentColor" />
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-2xl font-bold text-gray-900 dark:text-white mb-1"
            >
              {APP_NAME}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-sm text-gray-500 dark:text-gray-400 mb-6"
            >
              {isLogin ? t('auth.login.subtitle') : t('auth.register.subtitle')}
            </motion.p>
          </div>

          {/* Data Restored Indicator */}
          {dataRestored && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-sm text-green-800 dark:text-green-200"
            >
              {t('auth.formDataRestored')}
            </motion.div>
          )}

          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-3"
            >
              <AlertCircle size={20} className="text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800 dark:text-red-200">{t('auth.authenticationError')}</p>
                <p className="text-sm text-red-600 dark:text-red-300 mt-1">{error}</p>
              </div>
            </motion.div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
              >
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('auth.register.name')}
                </label>
                <div className="relative">
                  <User
                    size={18}
                    className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500"
                  />
                  <Input
                    type="text"
                    name="name"
                    placeholder={t('auth.register.namePlaceholder')}
                    value={formData.name}
                    onChange={handleChange}
                    required={!isLogin}
                    className="pl-11 h-11"
                  />
                </div>
              </motion.div>
            )}

            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: isLogin ? 0.1 : 0.2 }}
            >
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('auth.login.email')}
              </label>
              <div className="relative">
                <Mail
                  size={18}
                  className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500"
                />
                <Input
                  type="email"
                  name="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="pl-11 h-11"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: isLogin ? 0.2 : 0.3 }}
            >
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('auth.login.password')}
                </label>
                {isLogin && (
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
                  >
                    {t('auth.login.forgotPassword')}
                  </button>
                )}
              </div>
              <div className="relative">
                <Lock
                  size={18}
                  className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 z-10"
                />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  placeholder={isLogin ? t('auth.login.passwordPlaceholder') : t('auth.register.passwordPlaceholder')}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="pl-11 pr-11 h-11"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {!isLogin && (
                <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                  {t('auth.register.passwordHint')}
                </p>
              )}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: isLogin ? 0.3 : 0.4 }}
              className="pt-2"
            >
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 text-base font-semibold shadow-lg hover:shadow-xl transition-shadow"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                    />
                    {isLogin ? t('auth.login.signingIn') : t('auth.register.creatingAccount')}
                  </span>
                ) : (
                  isLogin ? t('auth.login.signIn') : t('auth.register.createAccount')
                )}
              </Button>
            </motion.div>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200 dark:border-gray-700"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                {isLogin ? t('auth.login.dontHaveAccount') : t('auth.register.alreadyHaveAccount')}
              </span>
            </div>
          </div>

          {/* Toggle Login/Register */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-center"
          >
            <button
              onClick={() => {
                setIsLogin(!isLogin)
                setError('')
                // Keep email when toggling (user might have typed it)
                setFormData({ email: formData.email, password: '', name: formData.name })
              }}
              className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
            >
              {isLogin ? t('auth.login.signUp') : t('auth.register.signIn')}
            </button>
          </motion.div>

          {/* Footer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700 text-center"
          >
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {isLogin ? t('auth.signingIn') : t('auth.creatingAccount')}, you agree to our{' '}
              <a href="/terms" className="text-primary-600 dark:text-primary-400 hover:underline">
                {t('auth.termsOfService')}
              </a>{' '}
              and{' '}
              <a href="/privacy" className="text-primary-600 dark:text-primary-400 hover:underline">
                {t('auth.privacyPolicy')}
              </a>
            </p>
          </motion.div>
        </Card>
      </motion.div>
    </div>
  )
}

export default AuthPage

